import { current, produce } from 'immer'
import { random as _random } from 'lodash/fp'
import { v4 as uuidV4 } from 'uuid'

import { TemplateConfigInterface } from '@flowx/core-sdk'

import { closeChat } from './common.handler'
import {
  extract_author,
  extract_end,
  extract_log,
  extract_message,
  extract_next_steps,
  extract_prompts,
} from './stream.handler'

import {
  GenerateUiFromDocumentResponse,
  GenerateUiResponse,
} from '../../../agents/designer/api/designer.api'
import { DesignerServiceNew } from '../../../agents/designer/services/designer-new.service'
import { DesignerService } from '../../../agents/designer/services/designer.service'
import { AiDesignerActions } from '../../../agents/designer/store/designer.actions'
import { AiAgent, DataModelDraft, Message, MessageType } from '../../../common/ai.dictionary'
import { AiState } from '../../ai.state'

export const onExtractUIFromDocumentSuccess = (
  state: AiState,
  { response }: { response: GenerateUiFromDocumentResponse }
): AiState =>
  produce(state, (draft: AiState) => {
    draft.messages.push({
      message: `<b>Look what I found in the compliance document:</b> <p> <i>${response.response}</i> </p> I will use this information to build/modify the bussines process for you.`,
      type: MessageType.AiMsg,
      commited: true,
    })
    AiDesignerActions.generateUiStream({ prompt: response.response })
  })

export const onUiDesignerNodeAiClick = (
  state: AiState,
  {
    userTaskNavigationAreaId,
    selectedTemplateConfigId,
    uiDesignerContext,
  }: {
    userTaskNavigationAreaId: number
    selectedTemplateConfigId: number
    uiDesignerContext?: string
  }
): AiState =>
  produce(state, (draft: AiState) => {
    // closeChat(draft)
    if (canSelectAgent(state)) {
      draft.selectedDesignerUserTaskAreaId = userTaskNavigationAreaId
      draft.selectedDesignerTemplateConfigId = selectedTemplateConfigId
      const isUiTemplate = uiDesignerContext === 'reusableUiTemplate'
      const draftMessage = {
        message: isUiTemplate
          ? 'Describe the reusable UI template you want to generate. This template can be embedded across multiple user tasks.'
          : 'Describe the user interface content so I can generate items for it.',
        actions: {
          checkDocumentContext: state.permissions.canViewDocuments,
          imageUpload: state.permissions.canUploadDocuments,
          processUpload: false,
        },
        type: MessageType.SystemMsg,
      }
      draft.draftMessage = {
        message: draftMessage,
        payload: {
          action_type: isUiTemplate ? 'ui_template' : 'user_interface_modification',
          context: null,
          continue_editing: false,
        },
        selectedAgent: AiAgent.DESIGNER,
      }
    }
  })

export const onGenerateUiSuccess = (
  state: AiState,
  { response, dataModel }: { response: GenerateUiResponse; dataModel: any[] }
): AiState =>
  produce(state, (draft: AiState) => {
    if (response.chat) {
      draft.messages.push({
        message: response.chat,
        type: MessageType.SystemMsg,
        commited: true,
      })
    } else if (response.error_message) {
      draft.messages.push(generateChatErrorMessage(response.error_message))
    } else {
      draft.generatedUI = response.answer
      if (response.location) {
        draft.generatedLocationId = response.location
      } else if (draft.currentUI?.id) {
        draft.generatedLocationId = { ci: draft.currentUI.id, index: 0, prompt: '' }
      }
      draft.generatedUIDataModelChanges = DesignerService.extractDataModelChangesFromAiConfig(
        response.answer,
        response.mapping.mapped_fields.map((field) => ({
          ...field,
          dataModelEntity: dataModel.find(({ name }) => name === field.model),
        }))
      )
      draft.messages.push({
        message:
          'UI has been generated. You can tweak the results by telling me what to change or edit the preview directly before saving.',
        context: {
          ...response,
          answer: JSON.stringify(response.answer),
        },
        commited: true,
        type: MessageType.AiMsg,
      })
      if (draft.autonomyLevel === 2) {
        AiDesignerActions.keepUiDraft()
      }
    }
  })

export const onMapTemplateConfigComponentsToDataModel = (
  state: AiState,
  { templateConfig }: { templateConfig: TemplateConfigInterface[] }
): AiState =>
  produce(state, (draft: AiState) => {
    if (draft.generatedUIDataModelChanges) {
      draft.generatedUIDataModelChanges =
        DesignerService.updateDataModelWithTemplateConfigIdentifiers(
          draft.generatedUIDataModelChanges,
          templateConfig
        )
    }
  })

export const onKeepTemplateConfigDraftSuccess = (state: AiState): AiState =>
  produce(state, (draft: AiState) => {
    draft.messages.push({
      message: 'Keep the generated UI.',
      type: MessageType.UserMsg,
      commited: true,
    })
    draft.selectedPlan?.actions.forEach((action) => {
      if (action.agent === AiAgent.DESIGNER) {
        action.steps = action.steps.map((step) =>
          step.step_number === draft.currentPlanStep?.step_number
            ? { ...step, step_status: 'completed', executionOutput: draft.generatedUINew }
            : step
        )
      }
    })
    draft.generatedUI = null
    draft.generatedUINew = null
    draft.temporaryUI = null
    draft.generatedEditDiff = null
    draft.editDiffAppliedConfig = null
    draft.currentFullUI = null
    draft.messages[draft.messages.length - 1].commited = true
    if (state.generatedUIDataModelChanges) {
      draft.messages.push({
        message:
          'UI has been saved. Regarding the data model for the generated UI elements, I have found:',
        dataModel: state.generatedUIDataModelChanges,
        type: MessageType.SystemMsg,
        commited: true,
      })
      draft.generatedUIDataModelChanges = null
    }
    draft.currentPlanStep = null
  })

export const onUpdateDataModelOnLastMessage = (
  state: AiState,
  { dataModel }: { dataModel: DataModelDraft }
): AiState =>
  produce(state, (draft: AiState) => {
    draft.messages[draft.messages.length - 1].dataModel = dataModel
    draft.generatedUIDataModelChanges = dataModel
  })

export const onDiscardUIDesignerDraft = (state: AiState): AiState =>
  produce(state, (draft: AiState) => {
    if (state.zenMode) {
      draft.messages.push({
        message: 'Discard the generated UI.',
        type: MessageType.UserMsg,
        commited: true,
      })
      draft.selectedPlan?.actions.forEach((action) => {
        if (action.agent === AiAgent.DESIGNER) {
          action.steps = action.steps.map((step) =>
            step.step_number === draft.currentPlanStep?.step_number
              ? { ...step, step_status: 'default' }
              : step
          )
        }
      })
      draft.generatedUI = null
      draft.generatedUINew = null
      draft.temporaryUI = null
      draft.generatedEditDiff = null
      draft.editDiffAppliedConfig = null
      draft.currentFullUI = null
      draft.generatedUIDataModelChanges = null

      draft.currentPlanStep = null
    } else {
      draft.minimized = true
      closeChat(draft)
    }
  })

export const onGetAnswerEventStream = (
  state: AiState,
  { response, dataModel }: { response: any; dataModel: any[] }
): AiState =>
  produce(state, (draft: AiState) => {
    if (!current(draft).backgroundAiResponse) {
      draft.streamingAiResponse = true
      draft.pendingAiResponse = true
    }
    draft.pendingRouteToDesigner = null
    const { message, context, sources } = response
    const lastMessageDraft = draft.messages[draft.messages.length - 1]
    const answer = extract_message(message)
    const nextSteps = extract_next_steps(message)
    const end = extract_end(message)
    const log = extract_log(message)
    const prompts = extract_prompts(message)
    const author = extract_author(message)

    if (message.answer?.operation === 'session') {
      draft.sessionId = message.answer.thread_id
      if (message.answer.request_id) {
        draft.lastRequestId = message.answer.request_id
      }
    }
    // parse logs and register
    if (log) {
      draft.logs.unshift({
        agent: log.agent,
        message: log.debug.info,
        message_type: 'INFO',
        timestamp: log.timestamp,
        status: log.debug.status,
        step: log.debug.step,
      })
      draft.currentAITaskMessage = log.debug.info
      return
    }
    if ((message as any).answer) {
      parseAnswerMessage(draft, message)
    }
    if (end) {
      parseEndMessage(draft, message, dataModel)
      return
    }
    if (!answer && !nextSteps) {
      return
    }
    const isLastMessageOfTypeAi =
      lastMessageDraft &&
      lastMessageDraft?.type === MessageType.AiMsg &&
      !lastMessageDraft?.commited
    if (isLastMessageOfTypeAi) {
      Object.assign(lastMessageDraft, response)
      lastMessageDraft.message = nextSteps + answer
      lastMessageDraft.context = null
      lastMessageDraft.sources = sources
      lastMessageDraft.author = author
      lastMessageDraft.html_preview = null
      lastMessageDraft.prompts = prompts
      lastMessageDraft.type = MessageType.AiMsg
    } else {
      draft.messages.push({
        ...response,
        message,
        context,
        sources,
        type: MessageType.AiMsg,
      })
    }
  })

// Helpers functions - for streaming events

function parseAnswerMessage(draft: AiState, messagePayload): void {
  generateFromUI(draft, (messagePayload as any).answer)
}

function parseEndMessage(draft: AiState, message, dataModel): void {
  const messagePayload = message.end
  if (messagePayload.operation === 'extract_sections') {
    draft.generatingUi = true
    draft.temporaryUI = {
      interface: messagePayload.answer,
      sectionsDetails: messagePayload.answer.sc.map((section) => ({ sc: section, rs: [] })) || [],
    }
  }

  if (messagePayload.operation === 'ai_designer_html') {
    if (messagePayload?.answer) {
      draft.generatingUi = true
      draft.generatedHTML = messagePayload.answer.html_interface
      draft.messages[draft.messages.length - 1].html_preview = messagePayload.answer.html_interface
    }
  }
  if (messagePayload.operation === 'ai_img_designer') {
    if (messagePayload?.answer) {
      draft.generatingUi = true
      draft.generatedHTML = messagePayload.answer.html_interface
      draft.messages[draft.messages.length - 1].html_preview = messagePayload.answer.html_interface
    }
  }
  if (messagePayload.operation === 'ai_classify') {
    draft.generatingUi = true
    draft.reasoningLogs.unshift({
      agent: message.agent,
      message: message.end.answer.reasoning,
      message_type: 'REASONING',
      operation: 'CLASSIFY IMAGE',
    })
  }
  if (messagePayload.operation === 'check_greeting') {
    draft.generatingUi = true
    draft.reasoningLogs.unshift({
      agent: message.agent,
      message: message.end.answer.reasoning,
      message_type: 'REASONING',
      operation: 'CHECK USER PROMPT',
    })
  }
  if (messagePayload.operation === 'map_data_model') {
    draft.generatingUi = true
    const model_draft = DesignerServiceNew.extractDataModelChangesFromAiConfig(
      current(draft).generatedUINew,
      messagePayload.answer.mapped_fields
        .map((field) => ({
          ...field,
          dataModelEntity: dataModel.find(({ name }) => name === field.model),
        }))
        .filter((field) => field.dataModelEntity)
    )
    draft.generatedUIDataModelChanges = model_draft
    draft.reasoningLogs.unshift({
      agent: message.agent,
      message: message.end.answer.reasoning,
      message_type: 'REASONING',
      operation: 'DATA MODEL',
    })
  }

  if (messagePayload.operation === 'link_buttons') {
    draft.reasoningLogs.unshift({
      agent: message.agent,
      message: message.end.answer.reasoning,
      message_type: 'REASONING',
      operation: 'LINK BUTTONS',
    })
  }

  if (messagePayload.operation === 'initial_actions') {
    draft.messages[draft.messages.length - 1].commited = true
  }

  if (messagePayload.operation === 'generate_ui') {
    draft.reasoningLogs.unshift({
      agent: message.agent,
      message: message.end.answer.reasoning,
      message_type: 'REASONING',
      operation: 'GENERATE UI',
    })
    generateFromUI(draft, { generated_ui: (messagePayload as any).answer, render: true })
    const generatedUINew = current(draft).generatedUINew

    // Extract page list for UI Flow multi-page summary
    const pageRoots =
      generatedUINew?.components?.filter((c: any) => c.pId === 0 && c.ci === 'PG') || []
    const pageList = pageRoots.map((p: any, idx: number) => ({
      index: idx,
      key: p.k || `page${idx}`,
      label: p.lb || `Page ${idx + 1}`,
    }))

    draft.messages[draft.messages.length - 1] = {
      message:
        draft.messages[draft.messages.length - 1].message +
        `<br/> <br/> UI with the name **${message.end.answer?.interface?.it}** has been generated. You can tweak the results by telling me what to change or edit the preview directly before saving.`,
      context: {
        ...generatedUINew,
        answer: JSON.stringify(generatedUINew),
        uiFlowPages: pageList,
      },
      html_preview: draft.messages[draft.messages.length - 1].html_preview,
      type: MessageType.AiMsg,
    }

    draft.streamingAiResponse = false
    draft.pendingAiResponse = false
    draft.currentAITaskMessage = null
    draft.backgroundAiResponse = true
    draft.isAiInteractionAllowed = false
    draft.messages[draft.messages.length - 1].commited = true
  }

  if (messagePayload.operation === 'edit_ui_diff') {
    draft.reasoningLogs.unshift({
      agent: message.agent,
      message: message.end.answer.reasoning,
      message_type: 'REASONING',
      operation: 'EDIT UI',
    })
    draft.generatedEditDiff = messagePayload.answer
    // Apply diff to full (unstripped) template config for preview.
    // Use currentFullUI (base config) if available; otherwise fall back to
    // editDiffAppliedConfig (supports chained edits before Keep).
    const baseUI = current(draft).currentFullUI ?? current(draft).editDiffAppliedConfig
    if (baseUI) {
      const clonedUI = structuredClone
        ? structuredClone(baseUI)
        : JSON.parse(JSON.stringify(baseUI))
      const modified = DesignerServiceNew.applyDiffToTemplateConfig(
        clonedUI,
        messagePayload.answer,
        () => _random(Date.now(), Number.MAX_SAFE_INTEGER),
        uuidV4
      )
      draft.editDiffAppliedConfig = modified
    } else {
      console.warn(
        '[edit_ui_diff] No base UI available (currentFullUI and editDiffAppliedConfig are both null) — cannot apply diff to preview'
      )
    }
    draft.generatingUi = true
    draft.streamingAiResponse = false
    draft.pendingAiResponse = false
    draft.currentAITaskMessage = null
    draft.backgroundAiResponse = true
    draft.isAiInteractionAllowed = false
    draft.messages[draft.messages.length - 1] = {
      message:
        draft.messages[draft.messages.length - 1].message +
        `<br/> <br/> UI has been edited: **${message.end.answer.reasoning}**. You can tweak the results by telling me what to change or edit the preview directly before saving.`,
      type: MessageType.AiMsg,
      commited: true,
    }
  }
}

function generateFromUI(draft: AiState, messagePayload): void {
  if (!messagePayload) {
    return
  }
  if (!messagePayload.render) {
    return
  }
  if (
    messagePayload?.generated_ui &&
    (messagePayload?.generated_ui?.components || messagePayload?.generated_ui?.interface)
  ) {
    // Auto-switch to the latest page during streaming
    const prevPageCount =
      draft.generatedUINew?.components?.filter((c: any) => c.pId === 0 && c.ci === 'PG')?.length ||
      0
    draft.generatedUINew = messagePayload.generated_ui
    draft.generatingUi = true
    const newPageCount =
      messagePayload.generated_ui?.components?.filter((c: any) => c.pId === 0 && c.ci === 'PG')
        ?.length || 0
    // When a new page appears, auto-switch to show it
    if (newPageCount > prevPageCount && newPageCount > 1) {
      draft.selectedUiFlowPageIndex = newPageCount - 1
    }
  } else {
    draft.generatedUI = messagePayload.generated_ui
    draft.generatingUi = true
  }

  if (messagePayload?.map_data_model) {
    const tempUI = current(draft).temporaryUI
    const tempUINew = current(draft).generatedUINew
    if (!tempUI || !tempUINew) {
      return
    }
  }
  if (messagePayload?.location) {
    draft.generatedLocationId = messagePayload.location
  } else if (draft.currentUI?.id) {
    draft.generatedLocationId = { ci: draft.currentUI.id, index: 0, prompt: '' }
  }
}

// Helpers functions - maybe common for all agents

function canSelectAgent(state: AiState): boolean {
  const { selectedAgent, pendingAiResponse, pendingKeepDraftSave, messages } = state

  const isLoading = pendingAiResponse || pendingKeepDraftSave
  const isChangeableAgent =
    !selectedAgent || selectedAgent === AiAgent.ASSISTANT || hasOnlySystemMsg(messages)

  return !isLoading && isChangeableAgent
}
function hasOnlySystemMsg(messages: Message[]): boolean {
  return !messages.find(({ type }) => type !== MessageType.SystemMsg)
}

function generateChatErrorMessage(errorMsg, statusCode: number = 400): Message {
  const message = extractMessageFromError(errorMsg, statusCode)

  return {
    message,
    type: MessageType.ErrorMsg,
    actions: {
      startNewSession: true,
    },
    commited: true,
  }
}

function extractMessageFromError(
  error:
    | string
    | { title: string; defaultMessage: string; error_message: { client_message: string } },
  statusCode: number = 400
): string {
  const CONTINUE_MESSAGE = `You can continue the conversation or start a new chat.`

  if (statusCode >= 500 || statusCode === 0) {
    return `Something went wrong on our end. ${CONTINUE_MESSAGE}`
  } else {
    const DEFAULT_MESSAGE = `Something went wrong with your request. Please check the information you entered and try again.`

    if (typeof error === 'string') {
      return `${error} ${CONTINUE_MESSAGE}`
    }

    const serverMessage =
      error?.title ?? error?.defaultMessage ?? error?.error_message?.client_message

    return `${serverMessage ?? DEFAULT_MESSAGE} ${CONTINUE_MESSAGE}`
  }
}
